import { authToken, getUserMe } from '@gen2/api/auth/api';
import { getFeatureFlags } from '@gen2/api/feature-flag/api';
import useRouter from '@gen2/hooks/useRouter';
import { IAuthTokenParams } from '@gen2/types/auth';
import { TOrganisation } from '@gen2/types/organisation';
import { TUser } from '@gen2/types/user';
import {
  Client_ID,
  generateOauthCode,
  openRedirectUri,
  Redirect_Uri,
} from '@gen2/utils/auth';
import {
  getCookie,
  removeCookie,
  setCookie,
  DEFAULT_COOKIE_EXPIRATION_DAYS,
} from '@gen2/utils/cookies';
import { createContext, useEffect, useMemo, useReducer } from 'react';
import type { FC, ReactNode } from 'react';

export type TFeatureFlags = {
  captcha: boolean;
  request_submitted_notification: boolean;
  expand_and_collapse_feature: boolean;
  invite_reference_in_file_naming_convention: boolean;
  live_form_listbox_removal: boolean;
  live_form_signature_with_date: boolean;
  live_form_duplicate_fields: boolean;
  live_form_save_signature_in_browser: boolean;
  file_path_truncation: boolean;
  portal_authentication: boolean;
  private_messaging: boolean;
  billing_contact_settings: boolean;
  usage_and_billing: boolean;
  branding_color_pallet: boolean;
};

interface State {
  isInitialized: boolean;
  isAuthenticated: boolean;
  user: TUser | null;
  permissions: TOrganisation['permissions'];
  featureFlags: TFeatureFlags;
}

export interface AuthContextValue extends State {
  platform: 'JWT';
  authCode: () => Promise<void>;
  getToken: () => Promise<void>;
  logout: (params?: LogoutProps[]) => Promise<void>;
  initialize: () => Promise<void>;
}

interface AuthProviderProps {
  children: ReactNode;
}

enum ActionType {
  INITIALIZE = 'INITIALIZE',
  LOGIN = 'LOGIN',
  LOGOUT = 'LOGOUT',
}

type InitializeAction = {
  type: ActionType.INITIALIZE;
  payload: {
    isAuthenticated: boolean;
    user: TUser | null;
    featureFlags: TFeatureFlags;
  };
};

type LoginAction = {
  type: ActionType.LOGIN;
  payload: {
    user: TUser;
    featureFlags: TFeatureFlags;
  };
};

type LogoutAction = {
  type: ActionType.LOGOUT;
};

interface LogoutProps {
  [name: string]: string | boolean;
}

type Action = InitializeAction | LoginAction | LogoutAction;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Handler = (state: State, action: any) => State;

export const initialFeatureFlags: TFeatureFlags = {
  captcha: false,
  request_submitted_notification: false,
  expand_and_collapse_feature: false,
  invite_reference_in_file_naming_convention: false,
  live_form_listbox_removal: false,
  live_form_signature_with_date: false,
  live_form_duplicate_fields: false,
  live_form_save_signature_in_browser: false,
  file_path_truncation: false,
  portal_authentication: false,
  private_messaging: false,
  billing_contact_settings: false,
  usage_and_billing: false,
  branding_color_pallet: false,
};

const initialState: State = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  permissions: {} as TOrganisation['permissions'],
  featureFlags: initialFeatureFlags,
};

const handlers: Record<ActionType, Handler> = {
  INITIALIZE: (state: State, action: InitializeAction): State => {
    const { isAuthenticated, user, featureFlags } = action.payload;

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
      featureFlags,
    };
  },
  LOGIN: (state: State, action: LoginAction): State => {
    const { user, featureFlags } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
      featureFlags,
    };
  },
  LOGOUT: (state: State): State => ({
    ...state,
    isAuthenticated: false,
    user: null,
    featureFlags: initialFeatureFlags,
  }),
};

const reducer = (state: State, action: Action): State =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

export const AuthContext = createContext<AuthContextValue>({
  ...initialState,
  platform: 'JWT',
  authCode: () => Promise.resolve(),
  getToken: () => Promise.resolve(),
  initialize: () => Promise.resolve(),
  logout: () => Promise.resolve(),
});

export const AuthProvider: FC<AuthProviderProps> = (props) => {
  const { children } = props;
  const router = useRouter();
  const [state, dispatch] = useReducer(reducer, initialState);
  const permissions = useMemo(() => {
    if (!state.user || !state.user?.organisations?.[0]) {
      return {} as TOrganisation['permissions'];
    }

    return state.user.organisations[0].permissions;
  }, [state.user]);

  const validateUserAccess = (user: TUser) => {
    // Skip validation if MFA setup is required - user will be redirected to MFA setup
    if (user?.mfa_setup_required) {
      return true;
    }

    if (!user.organisations || user.organisations.length === 0) {
      router.navigate('/error/no-organisations');
      return false;
    }

    // If user has last_organisation, verify it exists and has accounts
    if (user.last_organisation) {
      const lastOrg = user.organisations.find(
        (org) => org.id === user.last_organisation,
      );
      if (!lastOrg) {
        router.navigate('/switch-organisations');
        return false;
      }
      if (lastOrg.accounts.length === 0) {
        router.navigate('/error/no-accounts');
        return false;
      }
      // If last_account is specified, verify it exists in the last_organisation
      if (
        user.last_account &&
        !lastOrg.accounts.some((acc) => acc.id === user.last_account)
      ) {
        router.navigate('/switch-organisations');
        return false;
      }
    } else {
      // If no last_organisation, check the first organization
      const currentOrg = user.organisations[0];
      if (currentOrg.accounts.length === 0) {
        router.navigate('/error/no-accounts');
        return false;
      }
    }

    return true;
  };

  const initialize = async () => {
    try {
      const accessToken = getCookie('accessToken');

      if (accessToken) {
        const { data } = await getUserMe();
        const user: TUser = data?.data?.user;

        if (!validateUserAccess(user)) {
          return;
        }

        sessionStorage.setItem('FI-USER-SESSION', JSON.stringify(user));

        // Only set organisation and account if MFA setup is not required
        if (!user?.mfa_setup_required) {
          if (user.last_account && user.last_organisation) {
            sessionStorage.setItem(
              'FI-ORGANISATION-ID',
              user.last_organisation,
            );
            sessionStorage.setItem('FI-ACCOUNT-ID', user.last_account);
          } else if (user.organisations?.[0]) {
            sessionStorage.setItem(
              'FI-ORGANISATION-ID',
              user.organisations[0].id,
            );
            sessionStorage.setItem(
              'FI-ACCOUNT-ID',
              user.organisations[0].accounts[0].id,
            );
          }
        }

        // Fetch feature flags
        const { data: featureFlagsData } = await getFeatureFlags();
        const featureFlags = featureFlagsData.data;

        dispatch({
          type: ActionType.INITIALIZE,
          payload: {
            isAuthenticated: true,
            user,
            featureFlags: featureFlags,
          },
        });
      } else {
        dispatch({
          type: ActionType.INITIALIZE,
          payload: {
            isAuthenticated: false,
            user: null,
            featureFlags: initialFeatureFlags,
          },
        });
      }
    } catch (err) {
      dispatch({
        type: ActionType.INITIALIZE,
        payload: {
          isAuthenticated: false,
          user: null,
          featureFlags: initialFeatureFlags,
        },
      });
    }
  };

  // Initialization and injection of user information into the application context
  useEffect(() => {
    initialize();
  }, []);

  const authCode = async (): Promise<void> => {
    try {
      const { state, challenge } = await generateOauthCode();
      openRedirectUri(state, challenge);
    } catch (error) {
      console.error('Oauth error ==>', error);
    }
  };

  const getToken = async () => {
    const currentLocation = new URL(globalThis.location.href);
    const authorizationCode = currentLocation.searchParams.get('code') ?? '';
    const stateFromLocation = currentLocation.searchParams.get('state') ?? '';
    const initialCodeVerifier = sessionStorage.getItem('code_verifier') ?? '';

    if (window.sessionStorage.getItem('state') !== stateFromLocation) {
      throw Error('Probable session hijacking attack!');
    }

    const params = new URLSearchParams();
    const queryParams: IAuthTokenParams = {
      client_id: String(Client_ID),
      grant_type: 'authorization_code',
      code: authorizationCode,
      code_verifier: initialCodeVerifier,
      redirect_uri: Redirect_Uri,
    };

    for (const param in queryParams) {
      params.append(param, queryParams[param as keyof IAuthTokenParams] ?? '');
    }

    const { data } = await authToken(params);
    setCookie('accessToken', data?.access_token, {
      days: DEFAULT_COOKIE_EXPIRATION_DAYS,
    });
    setCookie('refreshToken', data?.refresh_token, {
      days: DEFAULT_COOKIE_EXPIRATION_DAYS,
    });

    const { data: result } = await getUserMe();

    const user: TUser = {
      ...result?.data?.user,
    };

    if (!validateUserAccess(user)) {
      return;
    }

    // set the user's organisations
    sessionStorage.setItem('FI-USER-SESSION', JSON.stringify(user));

    // Only set organisation and account if MFA setup is not required
    if (!user?.mfa_setup_required && user.organisations?.[0]) {
      sessionStorage.setItem('FI-ORGANISATION-ID', user.organisations[0].id);
      sessionStorage.setItem(
        'FI-ACCOUNT-ID',
        user.organisations[0].accounts[0].id,
      );
    }

    // Fetch and store feature flags after login
    const { data: featureFlagsData } = await getFeatureFlags();
    const featureFlags = featureFlagsData.data;

    dispatch({
      type: ActionType.LOGIN,
      payload: {
        user,
        featureFlags: featureFlags,
      },
    });

    // Redirect to MFA setup if required
    if (user?.mfa_setup_required) {
      router.navigate(`/user-settings?tab=security`);
    } else {
      router.navigate(`/dashboard`);
    }
  };

  const clearSessionStorage = () => {
    sessionStorage.removeItem('FI-USER-SESSION');
    sessionStorage.removeItem('FI-ORGANISATION-ID');
    sessionStorage.removeItem('FI-ACCOUNT-ID');
  };

  const logout = async (params?: LogoutProps[]) => {
    removeCookie('accessToken');
    removeCookie('refreshToken');
    dispatch({ type: ActionType.LOGOUT });
    clearSessionStorage();

    let route = '/login';

    if (params) {
      const queryParams = new URLSearchParams();

      params.forEach((paramVal) => {
        const keys: string[] = Object.keys(paramVal);

        keys.forEach((key) => {
          queryParams.append(key, paramVal[key].toString());
        });
      });

      route = '/login?' + queryParams.toString();
    }

    router.navigate(route);
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        platform: 'JWT',
        authCode,
        getToken,
        logout,
        initialize,
        permissions,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const AuthConsumer = AuthContext.Consumer;
